import { TAvailabilityTimeSlot } from "@/lib/api/availabilityApi"

type WeeklySchedule = {
  monday?: TAvailabilityTimeSlot[]
  tuesday?: TAvailabilityTimeSlot[]
  wednesday?: TAvailabilityTimeSlot[]
  thursday?: TAvailabilityTimeSlot[]
  friday?: TAvailabilityTimeSlot[]
  saturday?: TAvailabilityTimeSlot[]
  sunday?: TAvailabilityTimeSlot[]
}

/**
 * Calculates start and end time from a time slot index
 * @param index - The time slot index (0-47 for 30-minute intervals covering 24 hours)
 * @returns Object containing formatted start and end times
 */
export function calculateTimeFromSlotIndex(index: number): {
  startTime: string
  endTime: string
  label: string
} {
  const totalMinutes = index * 30
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60

  const startTime = `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`

  const endTotalMinutes = (index + 1) * 30
  const endHours = Math.floor(endTotalMinutes / 60)
  const endMinutes = endTotalMinutes % 60
  const endTime = `${endHours.toString().padStart(2, "0")}:${endMinutes.toString().padStart(2, "0")}`

  return {
    startTime,
    endTime,
    label: `${startTime} - ${endTime}`,
  }
}

/**
 * Transform date_overrides from local timezone to UTC
 * 1. Ungroups the date_overrides Record into individual date-time pairs
 * 2. Converts time strings to full datetime strings (date + time)
 * 3. Converts all datetimes to UTC
 * 4. Re-groups by UTC date
 *
 * @param dateOverrides - Record with date strings as keys and time slots as values
 * @returns Transformed Record with UTC dates and UTC time slots
 *
 * @example
 * Input (Local time):
 * {
 *   "2025-12-24": [
 *     { start_time: "09:00:00", end_time: "17:00:00" },
 *     { start_time: "22:00:00", end_time: "23:30:00" }
 *   ]
 * }
 *
 * Output (UTC - if local is UTC-5):
 * {
 *   "2025-12-24": [
 *     { start_time: "14:00:00", end_time: "22:00:00" }
 *   ],
 *   "2025-12-25": [
 *     { start_time: "03:00:00", end_time: "04:30:00" }
 *   ]
 * }
 */
export function transformDateOverridesToUTC(
  dateOverrides: Record<string, TAvailabilityTimeSlot[]>
): TAvailabilityTimeSlot[] {
  const result: TAvailabilityTimeSlot[] = []

  // Process each date and its time slots
  Object.entries(dateOverrides).forEach(([localDateStr, slots]) => {
    slots.forEach((slot) => {
      // Parse time components (HH:mm format)
      const [startHours, startMinutes] = slot.start_time.split(":").map(Number)
      const [endHours, endMinutes] = slot.end_time.split(":").map(Number)

      // Parse date components
      const [year, month, day] = localDateStr.split("-").map(Number)

      // Create full datetime strings in local timezone
      const startLocalDate = new Date(
        year,
        month - 1,
        day,
        startHours,
        startMinutes
      )
      const endLocalDate = new Date(year, month - 1, day, endHours, endMinutes)

      // Convert to UTC and extract date and time components
      const startUTCDate = startLocalDate.toISOString()

      const endUTCTime = endLocalDate.toISOString()

      // Initialize array for this UTC date if it doesn't exist
      result.push({
        start_time: startUTCDate,
        end_time: endUTCTime,
      })
    })
  })

  return result
}

/**
 * Group time slot pairs by date into a Record object
 * Converts an array of UTC datetime strings into grouped local time slots by date
 * This is the inverse operation of transformDateOverridesToUTC
 *
 * @param timeSlots - Array of objects containing start_time and end_time as ISO datetime strings (UTC)
 * @returns Record grouped by local date strings with local time values
 *
 * @example
 * Input (UTC):
 * [
 *   { start_time: "2025-12-24T14:00:00.000Z", end_time: "2025-12-24T22:00:00.000Z" },
 *   { start_time: "2025-12-25T03:00:00.000Z", end_time: "2025-12-25T04:30:00.000Z" }
 * ]
 *
 * Output (Local time - if local is UTC-5):
 * {
 *   "2025-12-24": [
 *     { start_time: "09:00:00", end_time: "17:00:00" }
 *   ],
 *   "2025-12-24": [
 *     { start_time: "22:00:00", end_time: "23:30:00" }
 *   ]
 * }
 */
export function groupTimeSlotsByDate(
  timeSlots: Array<{
    start_time: string
    end_time: string
  }>
): Record<string, TAvailabilityTimeSlot[]> {
  const result: Record<string, TAvailabilityTimeSlot[]> = {}

  timeSlots.forEach((slot) => {
    // Parse UTC datetime strings to Date objects
    const startDate = new Date(slot.start_time)
    const endDate = new Date(slot.end_time)

    // Extract local date string (YYYY-MM-DD)
    const localDateStr = `${startDate.getFullYear()}-${String(startDate.getMonth() + 1).padStart(2, "0")}-${String(startDate.getDate()).padStart(2, "0")}`

    // Extract local time strings (HH:mm:ss)
    const startTime = `${String(startDate.getHours()).padStart(2, "0")}:${String(startDate.getMinutes()).padStart(2, "0")}:${String(startDate.getSeconds()).padStart(2, "0")}`
    const endTime = `${String(endDate.getHours()).padStart(2, "0")}:${String(endDate.getMinutes()).padStart(2, "0")}:${String(endDate.getSeconds()).padStart(2, "0")}`

    // Initialize array for this date if it doesn't exist
    if (!result[localDateStr]) {
      result[localDateStr] = []
    }

    // Add the time slot to the appropriate date group
    result[localDateStr].push({
      start_time: startTime,
      end_time: endTime,
    })
  })

  return result
}

/**
 * Transform weekly_schedule from local timezone to UTC
 * Handles cases where time slots may shift to different weekdays when converted to UTC
 * Also handles slots that span across midnight in UTC by splitting them into two slots
 *
 * @param weeklySchedule - Weekly schedule with local time slots for each day
 * @returns Transformed weekly schedule with UTC times, potentially shifted to different days
 *
 * @example
 * Input (Local time UTC-5):
 * {
 *   monday: [{ start_time: "01:00", end_time: "23:00" }]
 * }
 *
 * Output (UTC):
 * {
 *   monday: [{ start_time: "06:00", end_time: "23:59" }],
 *   tuesday: [{ start_time: "00:00", end_time: "04:00" }]
 * }
 */
export function transformWeeklyScheduleToUTC(
  weeklySchedule: WeeklySchedule
): WeeklySchedule {
  const result: WeeklySchedule = {
    monday: [],
    tuesday: [],
    wednesday: [],
    thursday: [],
    friday: [],
    saturday: [],
    sunday: [],
  }

  const dayNames: Array<keyof WeeklySchedule> = [
    "sunday",
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
  ]

  // Map day names to their numeric values (0 = Sunday, 1 = Monday, etc.)
  const dayNameToIndex: Record<keyof WeeklySchedule, number> = {
    sunday: 0,
    monday: 1,
    tuesday: 2,
    wednesday: 3,
    thursday: 4,
    friday: 5,
    saturday: 6,
  }

  // Process each day and its time slots
  Object.entries(weeklySchedule).forEach(([dayName, slots]) => {
    if (!slots || slots.length === 0) return

    const localDayIndex = dayNameToIndex[dayName as keyof WeeklySchedule]

    slots.forEach((slot) => {
      // Parse time components (HH:mm format)
      const [startHours, startMinutes] = slot.start_time.split(":").map(Number)
      const [endHours, endMinutes] = slot.end_time.split(":").map(Number)

      // Create a reference date in the current week for this day
      // We'll use a fixed week as reference (the week of Jan 1, 2024 starts on Monday)
      const referenceWeekStart = new Date(2024, 0, 1) // Jan 1, 2024 is a Monday
      const referenceDayOffset = localDayIndex === 0 ? 6 : localDayIndex - 1 // Adjust for Sunday
      const referenceDate = new Date(referenceWeekStart)
      referenceDate.setDate(referenceWeekStart.getDate() + referenceDayOffset)

      // Create full datetime in local timezone
      const startLocalDate = new Date(
        referenceDate.getFullYear(),
        referenceDate.getMonth(),
        referenceDate.getDate(),
        startHours,
        startMinutes
      )

      const endLocalDate = new Date(
        referenceDate.getFullYear(),
        referenceDate.getMonth(),
        referenceDate.getDate(),
        endHours,
        endMinutes
      )

      // Convert to UTC
      const startUTCDate = new Date(startLocalDate.toISOString())
      const endUTCDate = new Date(endLocalDate.toISOString())

      // Determine which UTC day the start and end times belong to
      const startUTCDayIndex = startUTCDate.getUTCDay()
      const endUTCDayIndex = endUTCDate.getUTCDay()

      // Extract UTC time strings (HH:mm format)
      const startUTCTime = `${String(startUTCDate.getUTCHours()).padStart(2, "0")}:${String(startUTCDate.getUTCMinutes()).padStart(2, "0")}`
      const endUTCTime = `${String(endUTCDate.getUTCHours()).padStart(2, "0")}:${String(endUTCDate.getUTCMinutes()).padStart(2, "0")}`

      // Check if the slot spans across midnight (start and end are on different UTC days)
      if (startUTCDayIndex !== endUTCDayIndex) {
        // Split the slot into two parts:
        // 1. First part: start_time to 23:59 on the start day
        const startDayName = dayNames[startUTCDayIndex]
        result[startDayName]!.push({
          start_time: startUTCTime,
          end_time: "23:59",
        })

        // 2. Second part: 00:00 to end_time on the end day
        const endDayName = dayNames[endUTCDayIndex]
        result[endDayName]!.push({
          start_time: "00:00",
          end_time: endUTCTime,
        })
      } else {
        // Slot is within the same UTC day
        const utcDayName = dayNames[startUTCDayIndex]
        result[utcDayName]!.push({
          start_time: startUTCTime,
          end_time: endUTCTime,
        })
      }
    })
  })

  // Merge consecutive/adjacent time slots for each day
  Object.keys(result).forEach((key) => {
    const dayKey = key as keyof WeeklySchedule
    const slots = result[dayKey]
    if (!slots || slots.length <= 1) return

    // Sort slots by start time
    slots.sort((a, b) => {
      const [aHours, aMinutes] = a.start_time.split(":").map(Number)
      const [bHours, bMinutes] = b.start_time.split(":").map(Number)
      return aHours * 60 + aMinutes - (bHours * 60 + bMinutes)
    })

    // Merge overlapping or adjacent slots
    const mergedSlots: TAvailabilityTimeSlot[] = []
    let currentSlot = { ...slots[0] }

    for (let i = 1; i < slots.length; i++) {
      const nextSlot = slots[i]

      // Convert times to minutes for comparison
      const [currentEndHours, currentEndMinutes] = currentSlot.end_time
        .split(":")
        .map(Number)
      const [nextStartHours, nextStartMinutes] = nextSlot.start_time
        .split(":")
        .map(Number)

      const currentEndInMinutes = currentEndHours * 60 + currentEndMinutes
      const nextStartInMinutes = nextStartHours * 60 + nextStartMinutes

      // Check if slots are adjacent (within 1 minute) or overlapping
      if (nextStartInMinutes <= currentEndInMinutes + 1) {
        // Merge: extend current slot's end time to the later end time
        const [nextEndHours, nextEndMinutes] = nextSlot.end_time
          .split(":")
          .map(Number)
        const nextEndInMinutes = nextEndHours * 60 + nextEndMinutes

        if (nextEndInMinutes > currentEndInMinutes) {
          currentSlot.end_time = nextSlot.end_time
        }
      } else {
        // No overlap, push current slot and start a new one
        mergedSlots.push(currentSlot)
        currentSlot = { ...nextSlot }
      }
    }
    // Push the last slot
    mergedSlots.push(currentSlot)

    result[dayKey] = mergedSlots
  })

  // Clean up empty arrays
  Object.keys(result).forEach((key) => {
    const dayKey = key as keyof WeeklySchedule
    if (result[dayKey]?.length === 0) {
      delete result[dayKey]
    }
  })

  return result
}

/**
 * Transform weekly_schedule from UTC timezone to local timezone
 * This is the inverse operation of transformWeeklyScheduleToUTC
 * Handles cases where time slots may shift to different weekdays when converted from UTC
 * Also handles slots that span across midnight in local time by splitting them into two slots
 *
 * @param weeklySchedule - Weekly schedule with UTC time slots for each day
 * @returns Transformed weekly schedule with local times, potentially shifted to different days
 *
 * @example
 * Input (UTC):
 * {
 *   monday: [{ start_time: "06:00", end_time: "23:59" }],
 *   tuesday: [{ start_time: "00:00", end_time: "04:00" }]
 * }
 *
 * Output (Local time UTC-5):
 * {
 *   monday: [{ start_time: "01:00", end_time: "18:59" }],
 *   monday: [{ start_time: "19:00", end_time: "23:00" }]
 * }
 */
export function transformWeeklyScheduleToLocal(
  weeklySchedule: WeeklySchedule
): WeeklySchedule {
  const result: WeeklySchedule = {
    monday: [],
    tuesday: [],
    wednesday: [],
    thursday: [],
    friday: [],
    saturday: [],
    sunday: [],
  }

  const dayNames: Array<keyof WeeklySchedule> = [
    "sunday",
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
  ]

  // Map day names to their numeric values (0 = Sunday, 1 = Monday, etc.)
  const dayNameToIndex: Record<keyof WeeklySchedule, number> = {
    sunday: 0,
    monday: 1,
    tuesday: 2,
    wednesday: 3,
    thursday: 4,
    friday: 5,
    saturday: 6,
  }

  // Process each day and its time slots
  Object.entries(weeklySchedule).forEach(([dayName, slots]) => {
    if (!slots || slots.length === 0) return

    const utcDayIndex = dayNameToIndex[dayName as keyof WeeklySchedule]

    slots.forEach((slot) => {
      // Parse time components (HH:mm format)
      const [startHours, startMinutes] = slot.start_time.split(":").map(Number)
      const [endHours, endMinutes] = slot.end_time.split(":").map(Number)

      // Create a reference date in the current week for this day
      // We'll use a fixed week as reference (the week of Jan 1, 2024 starts on Monday)
      const referenceWeekStart = new Date(Date.UTC(2024, 0, 1)) // Jan 1, 2024 UTC
      const referenceDayOffset = utcDayIndex === 0 ? 6 : utcDayIndex - 1 // Adjust for Sunday
      const referenceDate = new Date(referenceWeekStart)
      referenceDate.setUTCDate(
        referenceWeekStart.getUTCDate() + referenceDayOffset
      )

      // Create full datetime in UTC timezone
      const startUTCDate = new Date(
        Date.UTC(
          referenceDate.getUTCFullYear(),
          referenceDate.getUTCMonth(),
          referenceDate.getUTCDate(),
          startHours,
          startMinutes
        )
      )

      const endUTCDate = new Date(
        Date.UTC(
          referenceDate.getUTCFullYear(),
          referenceDate.getUTCMonth(),
          referenceDate.getUTCDate(),
          endHours,
          endMinutes
        )
      )

      // Convert to local timezone by using local getters
      const startLocalDayIndex = startUTCDate.getDay()
      const endLocalDayIndex = endUTCDate.getDay()

      // Extract local time strings (HH:mm format)
      const startLocalTime = `${String(startUTCDate.getHours()).padStart(2, "0")}:${String(startUTCDate.getMinutes()).padStart(2, "0")}`
      const endLocalTime = `${String(endUTCDate.getHours()).padStart(2, "0")}:${String(endUTCDate.getMinutes()).padStart(2, "0")}`

      // Check if the slot spans across midnight (start and end are on different local days)
      if (startLocalDayIndex !== endLocalDayIndex) {
        // Split the slot into two parts:
        // 1. First part: start_time to 23:59 on the start day
        const startDayName = dayNames[startLocalDayIndex]
        result[startDayName]!.push({
          start_time: startLocalTime,
          end_time: "23:59",
        })

        // 2. Second part: 00:00 to end_time on the end day
        const endDayName = dayNames[endLocalDayIndex]
        result[endDayName]!.push({
          start_time: "00:00",
          end_time: endLocalTime,
        })
      } else {
        // Slot is within the same local day
        const localDayName = dayNames[startLocalDayIndex]
        result[localDayName]!.push({
          start_time: startLocalTime,
          end_time: endLocalTime,
        })
      }
    })
  })

  // Merge consecutive/adjacent time slots for each day
  Object.keys(result).forEach((key) => {
    const dayKey = key as keyof WeeklySchedule
    const slots = result[dayKey]
    if (!slots || slots.length <= 1) return

    // Sort slots by start time
    slots.sort((a, b) => {
      const [aHours, aMinutes] = a.start_time.split(":").map(Number)
      const [bHours, bMinutes] = b.start_time.split(":").map(Number)
      return aHours * 60 + aMinutes - (bHours * 60 + bMinutes)
    })

    // Merge overlapping or adjacent slots
    const mergedSlots: TAvailabilityTimeSlot[] = []
    let currentSlot = { ...slots[0] }

    for (let i = 1; i < slots.length; i++) {
      const nextSlot = slots[i]

      // Convert times to minutes for comparison
      const [currentEndHours, currentEndMinutes] = currentSlot.end_time
        .split(":")
        .map(Number)
      const [nextStartHours, nextStartMinutes] = nextSlot.start_time
        .split(":")
        .map(Number)

      const currentEndInMinutes = currentEndHours * 60 + currentEndMinutes
      const nextStartInMinutes = nextStartHours * 60 + nextStartMinutes

      // Check if slots are adjacent (within 1 minute) or overlapping
      if (nextStartInMinutes <= currentEndInMinutes + 1) {
        // Merge: extend current slot's end time to the later end time
        const [nextEndHours, nextEndMinutes] = nextSlot.end_time
          .split(":")
          .map(Number)
        const nextEndInMinutes = nextEndHours * 60 + nextEndMinutes

        if (nextEndInMinutes > currentEndInMinutes) {
          currentSlot.end_time = nextSlot.end_time
        }
      } else {
        // No overlap, push current slot and start a new one
        mergedSlots.push(currentSlot)
        currentSlot = { ...nextSlot }
      }
    }
    // Push the last slot
    mergedSlots.push(currentSlot)

    result[dayKey] = mergedSlots
  })

  // Clean up empty arrays
  Object.keys(result).forEach((key) => {
    const dayKey = key as keyof WeeklySchedule
    if (result[dayKey]?.length === 0) {
      delete result[dayKey]
    }
  })

  return result
}
